
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfileViews } from '@/hooks/useProfileViews';
import { useProfileViewsAccess } from '@/hooks/useProfileViewsAccess';
import ConnectionCard, { Profile } from '@/components/connections/ConnectionCard';
import BlurredProfileViewsOverlay from './BlurredProfileViewsOverlay';
import { Eye } from 'lucide-react';
import type { ProfileView } from '@/types/profile-views';

const ProfileViewsSection: React.FC = () => {
  const navigate = useNavigate();
  const { views, loading, error } = useProfileViews();
  const { shouldShowBlur, loading: accessLoading } = useProfileViewsAccess();

  const transformViewToProfile = (view: ProfileView): Profile | null => {
    if (!view.viewer_profile) return null;

    return {
      id: view.viewer_profile.id,
      name: view.viewer_profile.username,
      username: view.viewer_profile.username,
      age: view.viewer_profile.age || 0,
      photo: view.viewer_profile.main_photo_url,
      location: view.viewer_profile.location || '',
      occupation: view.viewer_profile.occupation || '',
      connectionDate: view.created_at,
      interests: []
    };
  };

  const handleProfileAction = (id: string | number, actionType: 'like' | 'unlike' | 'message') => {
    if (actionType === 'message' || actionType === 'like') {
      navigate(`/main/profile/${id}`);
    }
  };

  if (loading || accessLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="h-64 bg-gray-800 rounded-xl"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Eye className="h-12 w-12 text-white/30 mx-auto mb-4" />
        <p className="text-red-400 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (views.length === 0) {
    return (
      <div className="text-center py-12">
        <Eye className="h-16 w-16 text-white/30 mx-auto mb-4" />
        <h3 className="text-white text-lg font-medium mb-2">
          Nenhuma visualização ainda
        </h3>
        <p className="text-white/60 max-w-md mx-auto">
          Seu perfil ainda não foi visualizado. Em breve, alguém especial vai aparecer por aqui.
        </p>
      </div>
    );
  }

  const profileViews = views
    .map(transformViewToProfile)
    .filter((view): view is Profile => view !== null);

  return (
    <div className="relative">
      <BlurredProfileViewsOverlay isVisible={shouldShowBlur} />
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {profileViews.map((profile) => (
          <ConnectionCard
            key={profile.id}
            profile={profile}
            isMatch={false}
            isSentLike={false}
            isReceived={false}
            onAction={handleProfileAction}
          />
        ))}
      </div>
    </div>
  );
};

export default ProfileViewsSection;
